import { DialogRoute } from 'bernie-core';
import { Layer } from 'bernie-view';
import * as React from 'react';
import { formatText } from 'bernie-l10n';
import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkToolbar, ToolbarHeadingLevel, ToolbarType } from 'uitk-react-toolbar';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { getFilteredObjectKeys, getPageId } from '../../../helpers/common-helper';
import { useStores } from '../../../utils/hooks';
import { PrimaryButtonTracked, TertiaryButtonTracked } from '../../../utils/click-trackers';
import { ActivitySort } from '../../../common/__generated__/api';
import { getUpdatedFilterQueryObject } from './filter-helper';
import { SortFilter } from './sort-filter';
import { SortFilterDialogButton } from './sort-filter-dialog-button';

export interface SortFilterDialogProps {
  loading?: boolean;
}

export const SortFilterDialog: React.FC<SortFilterDialogProps> = ({ loading }): JSX.Element => {
  const { analytics, activitySearchStore, routeStore } = useStores();
  const pageId = getPageId(activitySearchStore);
  const focusField: React.RefObject<any> = React.useRef();

  const setFocusOnSortFilterButton = (): void => {
    if (focusField) {
      const node = focusField.current;
      if (node) {
        const sortButton = node.querySelector('#sort-and-filter-button') as HTMLElement;
        if (sortButton) {
          sortButton.focus();
        }
      }
    }
  };

  const handleSortAndFilterDialogClose = (): void => {
    routeStore.updateSearch({ pwaDialog: '' });
    setFocusOnSortFilterButton();
  };

  const createQueryString = (filterKeys: any[], pwaDialog: any): string =>
    routeStore.mergeQueryString(routeStore.search, {
      sort: activitySearchStore.sortAndFilterObject.initialSelectedSort,
      slimSearchKeyword: activitySearchStore.sortAndFilterObject.keywordValue,
      pwaDialog: pwaDialog,
      ...getUpdatedFilterQueryObject(activitySearchStore)
    });

  const handleClick = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const filterKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters);

    const newQueryString = createQueryString(filterKeys, '');

    if (activitySearchStore.sortAndFilterObject.keywordValue.length > 0) {
      analytics.trackEvent(
        `${pageId}.filterSort.SearchActivity`,
        'keyword-search-done',
        analytics.omnitureData,
        null,
        true
      );
    }

    setFocusOnSortFilterButton();
    routeStore.updateSearch(newQueryString, {}, true);
  };

  const clearForm = (): void => {
    activitySearchStore.sortAndFilterObject.initialSelectedSort = ActivitySort.RECOMMENDED;
    activitySearchStore.sortAndFilterObject.keywordValue = '';
    const filterCategoryKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters);
    filterCategoryKeys.forEach(filterCategoryKey => {
      const filterKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters[filterCategoryKey]);
      filterKeys.forEach(
        filterKey => (activitySearchStore.sortAndFilterObject.filters[filterCategoryKey][filterKey].selected = false)
      );
    });
    activitySearchStore.sortAndFilterObject.initialSeeAllOption = false;

    const newQueryString = createQueryString(filterCategoryKeys, 'filter');
    routeStore.updateSearch(newQueryString, {}, true);
  };

  return (
    <fieldset
      className="all-t-margin-two all-x-padding-three"
      disabled={loading}
      aria-busy={loading}
      ref={focusField}
      data-testid="sort-filter-dialog--fieldset"
    >
      <DialogRoute id="filter">
        <Layer id="dialog-filter">
          <UitkFullscreenDialog
            data-testid="sort-filter-dialog--fullscreen"
            closeCallback={handleSortAndFilterDialogClose}
            dialogShow
            strictFullscreen
          >
            <UitkToolbar
              key="sort-filter-dialog--toolbar"
              action={(): void => handleSortAndFilterDialogClose()}
              type={ToolbarType.CLOSE}
              iconLabel="Close the dialog"
              headingLevel={ToolbarHeadingLevel.TWO}
              actionContent={
                <TertiaryButtonTracked
                  key="sort-filter-dialog-clear-button"
                  type="reset"
                  text={formatText('lx.search.sortAndFilter.clear')}
                  onClick={clearForm}
                  rfrr={`LX.${pageId}.filterSort.ClearAll`}
                  linkName="srp-filters-sort-clear-all"
                  data-testid="sort-filter-dialog--clear-button"
                />
              }
              header={formatText('lx.search.sortAndFilter')}
            />
            <UitkDialogContent key="sort-filter-dialog-content" className="all-b-margin-twelve">
              <SortFilter inDialog={true} />
            </UitkDialogContent>
            <UitkScrim key="sort-filter-dialog-scrim" type={UitkScrimType.BUTTON}>
              <PrimaryButtonTracked
                data-testid="sort-filter-dialog--done-button"
                linkName={'Filter Drawer Done Clicked'}
                rfrr={`LX.${pageId}.filterSort.drawer.done`}
                type="button"
                onClick={handleClick}
                isFullWidth
              >
                {formatText('lx.label.done')}
              </PrimaryButtonTracked>
            </UitkScrim>
          </UitkFullscreenDialog>
        </Layer>
      </DialogRoute>
      <SortFilterDialogButton pageId={pageId} />
    </fieldset>
  );
};
